import React, { useCallback, useEffect, useState } from "react";
import AgoraRTC, {
  useRTCClient,
  useIsConnected,
  useClientEvent,
  LocalUser,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  RemoteUser,
  useRemoteUsers,
  useJoin,
  usePublish,
  LocalVideoTrack,
  AgoraRTCProvider,
} from "agora-rtc-react";
// import AgoraRTC from "agora-rtc-sdk-ng";

import { useStopwatch } from "react-timer-hook";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { authActions } from "../../../redux/auth";
import {
  ref,
  child,
  get,
  serverTimestamp,
  onChildChanged,
  update,
} from "firebase/database";
import { firebase_db } from "../../../FirebaseSetup";
import ApiService from "../../../services/ApiService";
import Swal from "sweetalert2";
import "../Call.css";

const AgoraManager = ({ config, location }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { userData, userId } = useSelector((state) => state.auth);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dispatch = useDispatch();

  const agoraEngine = useRTCClient();

  const navigate = useNavigate();
  const [MicMuted, setMicMuted] = useState(false);
  const [CameraOff, setCameraOff] = useState(false);
  const [Volumn, setVolumn] = useState(10);

  const { localCameraTrack } = useLocalCameraTrack();
  const { localMicrophoneTrack } = useLocalMicrophoneTrack();
  const isConnected = useIsConnected();

  const {
    totalSeconds,
    seconds,
    minutes,
    hours,
    days,
    isRunning,
    start,
    pause,
    reset,
  } = useStopwatch({ autoStart: true });

  useJoin({
    appid: config.appId,
    channel: config.channel,
    token: config.token,
  });

  usePublish([localMicrophoneTrack, localCameraTrack]);

  const remoteUsers = useRemoteUsers();

  // Event listener for user left
  useClientEvent(agoraEngine, "user-left", (user) => {
    console.log("The user", user.uid, "has left the channel");
    CallEnd();
  });

  // Event listener for user published
  useClientEvent(agoraEngine, "user-published", (user, mediaType) => {
    console.log("The user", user.uid, "has published media in the channel");
  });

  const toggleMic = () => {
    setMicMuted((prev) => !prev);
  };

  const Camera_Off_On = () => {
    setCameraOff((prev) => !prev);
  };

  const toggleSpeakerphone = useCallback(() => {
    if (Volumn === 10) {
      setVolumn(100);
    } else {
      setVolumn(10);
    }
  }, [Volumn]);

  const CallEnd = async () => {
    let new_data = {
      userid: location.state.snap_data.sender_id,
      call_duration: totalSeconds, // given value in second
      callerid: location.state.snap_data.receiver_id,
      genKey: location.state.room_id,
      call_type: "videocall", // video or audio
      login_user_id: userId
    };

    ///////////////////// Api call ///////////////////////
    var responce = await new ApiService().POST(`/balance-deduct-2`, new_data);

    ///// firebase update //////////////
    const updates = {};
    updates[
      `/user_video_audio_call_requests/${location.state.room_id}/status`
    ] = "complete";
    updates[
      `/user_video_audio_call_requests/${location.state.room_id}/created_at`
    ] = serverTimestamp();
    update(ref(firebase_db), updates);

    /////////////////// Api call ///////////////////////
    var responce_2 = await new ApiService().POST("/user-info", {
      userid: userData.id,
    });

    var resData = responce_2.data.user_details[0];
    dispatch(authActions.UpdateUserData(resData));

    Swal.fire(responce.data.message);

    console.log("call end");

    localCameraTrack?.close();
    localMicrophoneTrack?.close();

    navigate("/find-match", { replace: true });
  };

  // Cleanup – leave channel & close tracks on component unmount
  useEffect(() => {
    return () => {
      console.log('🧹 VideoCall unmount – leaving Agora channel');
      if (agoraEngine) {
        agoraEngine.leave?.();
      }
      localCameraTrack?.close?.();
      localMicrophoneTrack?.close?.();
    };
  }, []);

  // Stop the stopwatch after 5 minutes
  useEffect(() => {
    if (totalSeconds >= Number(location.state.snap_data.totalSeconds)) {
      pause();
      CallEnd();
    }
  }, [totalSeconds, pause]);

  // Lock body scrolling to ensure perfect full screen fit
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";

    return () => {
      document.body.style.overflow = originalOverflow === "hidden" ? "" : originalOverflow;
      document.body.style.height = originalHeight === "100vh" ? "" : originalHeight;
    };
  }, []);

  const remoteParticipant = userData?.id === location.state?.snap_data?.sender_id
    ? location.state?.snap_data?.receiver_details
    : location.state?.snap_data?.sender_details;

  return (
    <div className="call-screen-wrapper">
      {/* Header Overlay */}
      <div className="call-header">
        <div className="call-title-area">
          <img
            className="call-avatar"
            src={remoteParticipant?.profile_pic_pro_resize_200_url || "/images/step/01.png"}
            alt={remoteParticipant?.name || "Participant"}
          />
          <div className="call-meta">
            <h5>{remoteParticipant?.name || "Partner"}</h5>
          </div>
        </div>
      </div>

      {/* Timer HUD Badge */}
      <div className="timer-badge">
        <div className="pulse-icon"></div>
        <span>
          {String(hours).padStart(2, "0")}:{String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </span>
      </div>

      {/* Video Stage Area for Remote User */}
      <div className="video-stage">
        {remoteUsers.length > 0 ? (
          remoteUsers.map((user, index) => (
            index === 0 && (
              <div className="agora-video-view" key={user.uid}>
                <RemoteUser
                  user={user}
                  audioTrack={user.audioTrack}
                  videoTrack={user.videoTrack}
                  playAudio={true}
                  playVideo={true}
                  volume={Volumn}
                  style={{ width: "100%", height: "100%" }}
                  cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
                />
              </div>
            )
          ))
        ) : (
          <div className="viewport-loading">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
            <p>Waiting for your partner to join...</p>
          </div>
        )}
      </div>

      {/* Local Video - Picture in Picture */}
      {isConnected && (
        <div className="local-pip-wrapper">
          <LocalUser
            videoTrack={localCameraTrack}
            audioTrack={localMicrophoneTrack}
            cameraOn={!CameraOff}
            micOn={!MicMuted}
            playVideo={true}
            playAudio={false}
            style={{ width: "100%", height: "100%" }}
            cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
          />
        </div>
      )}

      {/* Bottom Controls Panel */}
      <div className="controls-bar">
        {/* Toggle Mic */}
        <div className="control-item-wrap">
          <button
            onClick={toggleMic}
            className={`control-circle-btn ${MicMuted ? "" : "active"}`}
            title={MicMuted ? "Unmute Mic" : "Mute Mic"}
          >
            {MicMuted ? (
              <i className="fa-solid fa-microphone-slash"></i>
            ) : (
              <i className="fa-solid fa-microphone"></i>
            )}
          </button>
          <span className="control-btn-label">{MicMuted ? "Mic Off" : "Mic On"}</span>
        </div>

        {/* Toggle Camera */}
        <div className="control-item-wrap">
          <button
            onClick={Camera_Off_On}
            className={`control-circle-btn ${CameraOff ? "" : "active"}`}
            title={CameraOff ? "Turn Camera On" : "Turn Camera Off"}
          >
            <i className="fa-solid fa-video"></i>
          </button>
          <span className="control-btn-label">{CameraOff ? "Camera Off" : "Camera On"}</span>
        </div>

        {/* Toggle Speaker/Volume */}
        <div className="control-item-wrap">
          <button
            onClick={toggleSpeakerphone}
            className={`control-circle-btn ${Volumn === 100 ? "active" : ""}`}
            title={Volumn === 100 ? "Speaker Off" : "Speaker On"}
          >
            {Volumn === 100 ? (
              <i className="fa-solid fa-volume-high"></i>
            ) : (
              <i className="fa-solid fa-volume-low"></i>
            )}
          </button>
          <span className="control-btn-label">{Volumn === 100 ? "Speaker High" : "Speaker Normal"}</span>
        </div>

        {/* End Call */}
        <div className="control-item-wrap">
          <button
            className="control-circle-btn danger"
            onClick={CallEnd}
            title="End Video Call"
          >
            <i className="fa-solid fa-phone-slash"></i>
          </button>
          <span className="control-btn-label">End Call</span>
        </div>
      </div>
    </div>
  );
};

// App Component
const Index = () => {
  const location = useLocation();

  const config = {
    // Your Agora App Config
    appId: process.env.REACT_APP_AGORA_APP_ID,
    channel: location.state.room_id,
    token: null,
  };

  return (
    <AgoraManager config={config} location={location} />
  );
};

export default Index;
